/**
 * Supplier Mutations Hook
 * Handles supplier CRUD operations with proper error handling and cache invalidation
 */

import { useLogger } from '@/lib/logger';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import type { CreateSupplierData } from '../types';

export function useSupplierMutations() {
  const { t } = useTranslation('orders');
  const logger = useLogger();
  const utils = trpc.useUtils();

  const createSupplier = trpc.suppliers.create.useMutation({
    onMutate: (supplierData: CreateSupplierData) => {
      logger.info('Creating supplier', {
        operation: 'create_supplier',
        supplierTitle: supplierData.title,
      });
    },
    onSuccess: (data) => {
      utils.suppliers.list.invalidate();

      logger.info('Supplier created successfully', {
        operation: 'create_supplier',
        supplierId: data.id,
      });

      toast({
        title: 'Success',
        description: t('toast.supplierCreated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'suppliers',
          operation: 'create_supplier',
        },
      });

      logger.error('Failed to create supplier', {
        operation: 'create_supplier',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedCreateSupplier'),
        variant: 'destructive',
      });
    },
  });

  const updateSupplier = trpc.suppliers.update.useMutation({
    onMutate: ({ supplierId }) => {
      logger.info('Updating supplier', {
        operation: 'update_supplier',
        supplierId,
      });
    },
    onSuccess: (data) => {
      utils.suppliers.list.invalidate();

      logger.info('Supplier updated successfully', {
        operation: 'update_supplier',
        supplierId: data.id,
      });

      toast({
        title: 'Success',
        description: t('toast.supplierUpdated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'suppliers',
          operation: 'update_supplier',
        },
      });

      logger.error('Failed to update supplier', {
        operation: 'update_supplier',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedUpdateSupplier'),
        variant: 'destructive',
      });
    },
  });

  const deactivateSupplier = trpc.suppliers.deactivate.useMutation({
    onMutate: ({ supplierId }) => {
      logger.info('Deactivating supplier', {
        operation: 'deactivate_supplier',
        supplierId,
      });
    },
    onSuccess: () => {
      utils.suppliers.list.invalidate();

      logger.info('Supplier deactivated successfully', {
        operation: 'deactivate_supplier',
      });

      toast({
        title: 'Success',
        description: t('toast.supplierDeactivated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'suppliers',
          operation: 'deactivate_supplier',
        },
      });

      logger.error('Failed to deactivate supplier', {
        operation: 'deactivate_supplier',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedDeactivateSupplier'),
        variant: 'destructive',
      });
    },
  });

  const reactivateSupplier = trpc.suppliers.reactivate.useMutation({
    onMutate: ({ supplierId }) => {
      logger.info('Reactivating supplier', {
        operation: 'reactivate_supplier',
        supplierId,
      });
    },
    onSuccess: () => {
      utils.suppliers.list.invalidate();

      logger.info('Supplier reactivated successfully', {
        operation: 'reactivate_supplier',
      });

      toast({
        title: 'Success',
        description: t('toast.supplierReactivated'),
      });
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'suppliers',
          operation: 'reactivate_supplier',
        },
      });

      logger.error('Failed to reactivate supplier', {
        operation: 'reactivate_supplier',
        error: error.message,
      });

      toast({
        title: 'Error',
        description: error.message || t('toast.failedReactivateSupplier'),
        variant: 'destructive',
      });
    },
  });

  return {
    createSupplier,
    updateSupplier,
    deactivateSupplier,
    reactivateSupplier,
  };
}
